import { useId, type FormEvent } from "react";
import type {
  Asset,
  AssetDraft,
  AssetStatus,
  AssetType,
  ClientOption,
} from "../../types";
import { serviceDefinitions, STATUSES } from "./assetConfig";
import styles from "./ClientAssetModule.module.css";

type Props = {
  type: AssetType;
  asset: Asset | AssetDraft;
  clients: ClientOption[];
  viewOnly: boolean;
  saving: boolean;
  onSetEditable: () => void;
  onClose: () => void;
  onSubmit: (asset: Asset | AssetDraft) => Promise<void>;
};

function numberOrNull(value: FormDataEntryValue | null) {
  const normalized = String(value ?? "").trim();
  if (!normalized) return null;
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
}

export default function AssetModal({
  type,
  asset,
  clients,
  viewOnly,
  saving,
  onSetEditable,
  onClose,
  onSubmit,
}: Props) {
  const id = useId();
  const definitions = serviceDefinitions(type);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (viewOnly || saving) return;

    const form = new FormData(event.currentTarget);
    const client = clients.find(
      (option) => option.id === String(form.get("clientId") ?? ""),
    );
    if (!client) throw new Error("Select a client before saving.");

    const services = { ...asset.services };
    definitions.forEach(({ key }) => {
      services[key] = {
        lastService: String(form.get(`service_${key}_last`) ?? ""),
        nextService: String(form.get(`service_${key}_next`) ?? ""),
      };
    });

    await onSubmit({
      ...asset,
      assetId: String(form.get("assetId") ?? "").trim(),
      type,
      clientId: client.id,
      clientCode: client.code,
      clientName: client.name,
      name: String(form.get("name") ?? "").trim(),
      vessel: String(form.get("vessel") ?? "").trim(),
      capacity: numberOrNull(form.get("capacity")),
      unitCount: Math.max(1, numberOrNull(form.get("unitCount")) ?? 1),
      manufacturer: String(form.get("manufacturer") ?? "").trim(),
      serial: String(form.get("serial") ?? "").trim(),
      imoNumber: String(form.get("imoNumber") ?? "").trim(),
      model: String(form.get("model") ?? "").trim(),
      location: String(form.get("location") ?? "").trim(),
      yearOfManufacture: numberOrNull(form.get("yearOfManufacture")),
      status: String(form.get("status") ?? "Operational") as AssetStatus,
      services,
      notes: String(form.get("notes") ?? "").trim(),
    });
  }

  return (
    <div className={styles.modalBackdrop} onMouseDown={onClose}>
      <section
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className={styles.modalHeader}>
          <div>
            <p>{type.toUpperCase()} RECORD</p>
            <h2 id={`${id}-title`}>
              {viewOnly
                ? asset.assetId || `${type} details`
                : "id" in asset
                  ? `Edit ${asset.assetId}`
                  : `Add ${type}`}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close dialog">
            ×
          </button>
        </header>

        <form onSubmit={submit}>
          <div className={styles.formGrid}>
            <label>
              <span>Client</span>
              <select
                name="clientId"
                defaultValue={asset.clientId}
                disabled={viewOnly}
                required
              >
                <option value="" disabled>
                  Select client
                </option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Asset code</span>
              <input
                name="assetId"
                defaultValue={asset.assetId}
                readOnly={viewOnly}
                required
              />
            </label>
            <label>
              <span>Platform / vessel</span>
              <input
                name="vessel"
                defaultValue={asset.vessel}
                readOnly={viewOnly}
                required
              />
            </label>
            <label>
              <span>Asset name</span>
              <input
                name="name"
                defaultValue={asset.name}
                readOnly={viewOnly}
                required
              />
            </label>
            <label>
              <span>Model</span>
              <input name="model" defaultValue={asset.model} readOnly={viewOnly} />
            </label>
            <label>
              <span>Manufacturer / brand</span>
              <input
                name="manufacturer"
                defaultValue={asset.manufacturer}
                readOnly={viewOnly}
                required
              />
            </label>
            <label>
              <span>Serial number</span>
              <input
                name="serial"
                defaultValue={asset.serial}
                readOnly={viewOnly}
              />
            </label>
            <label>
              <span>IMO number</span>
              <input
                name="imoNumber"
                defaultValue={asset.imoNumber}
                readOnly={viewOnly}
              />
            </label>
            <label>
              <span>Units</span>
              <input
                name="unitCount"
                type="number"
                min="1"
                defaultValue={asset.unitCount}
                readOnly={viewOnly}
                required
              />
            </label>
            <label>
              <span>Capacity (pax)</span>
              <input
                name="capacity"
                type="number"
                min="1"
                defaultValue={asset.capacity ?? ""}
                readOnly={viewOnly}
              />
            </label>
            <label>
              <span>Current location</span>
              <input
                name="location"
                defaultValue={asset.location}
                readOnly={viewOnly}
                required
              />
            </label>
            <label>
              <span>Year of manufacture</span>
              <input
                name="yearOfManufacture"
                type="number"
                min="1900"
                max="2100"
                defaultValue={asset.yearOfManufacture ?? ""}
                readOnly={viewOnly}
              />
            </label>
            <label>
              <span>Status</span>
              <select name="status" defaultValue={asset.status} disabled={viewOnly}>
                {STATUSES.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </label>

            <div className={`${styles.wide} ${styles.serviceSection}`}>
              <strong>Service schedule</strong>
              <span>
                The scheduled worker checks each next-service date for the 14-day
                and 7-day email windows.
              </span>
            </div>

            {definitions.map(({ key, label }) => (
              <div className={styles.servicePair} key={key}>
                <strong>{label}</strong>
                <label>
                  <span>Last service</span>
                  <input
                    name={`service_${key}_last`}
                    type="date"
                    defaultValue={asset.services[key]?.lastService ?? ""}
                    readOnly={viewOnly}
                  />
                </label>
                <label>
                  <span>Next service</span>
                  <input
                    name={`service_${key}_next`}
                    type="date"
                    defaultValue={asset.services[key]?.nextService ?? ""}
                    readOnly={viewOnly}
                  />
                </label>
              </div>
            ))}

            <label className={styles.wide}>
              <span>Notes</span>
              <textarea
                name="notes"
                defaultValue={asset.notes}
                readOnly={viewOnly}
                placeholder="Add service notes or other important details…"
              />
            </label>
          </div>

          <div className={styles.formActions}>
            <button type="button" onClick={onClose}>
              Close
            </button>
            {viewOnly ? (
              <button type="button" className={styles.primary} onClick={onSetEditable}>
                Edit record
              </button>
            ) : (
              <button type="submit" className={styles.primary} disabled={saving}>
                {saving ? "Saving…" : "Save record"}
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
